// Updated: 2026-08-23 13:20:00 CDT
// Purpose: Convert screenplay notation and display-only capitalization into natural speech without changing source content.

import type { SequenceItem } from "@/types/screenplay";

// Fountain/plain-text screenplays use -- for the same pause as an em dash.
// Keep sentence splitting, selection playback and emotion fragments consistent;
// a single hyphen inside a word or identifier must remain untouched.
export const SPEECH_DASH_BOUNDARY = /\s*(?:[—–]|-{2,})\s*/gu;

const capitalizeSentenceStarts = (value: string): string =>
  value.replace(/(^|[.!?]\s+)([a-z])/g, (_match, boundary: string, letter: string) =>
    `${boundary}${letter.toUpperCase()}`,
  );

const ensureTerminalPunctuation = (value: string): string =>
  /[.!?]$/.test(value) ? value : `${value}.`;

const SMALL_NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
] as const;

const TENS_WORDS = ["", "", "twenty", "thirty", "forty", "fifty"] as const;

const numberBelowSixtyToWords = (value: number): string => {
  if (value < 20) return SMALL_NUMBER_WORDS[value];

  const tens = Math.floor(value / 10);
  const units = value % 10;
  return units === 0
    ? TENS_WORDS[tens]
    : `${TENS_WORDS[tens]} ${SMALL_NUMBER_WORDS[units]}`;
};

const normalizeClockTimes = (value: string): string =>
  value.replace(
    /\b(1[0-2]|0?[1-9]):([0-5]\d)\b/g,
    (_match, hourText: string, minuteText: string) => {
      const hour = Number(hourText);
      const minute = Number(minuteText);
      const spokenMinute = minute === 0
        ? "o'clock"
        : minute < 10
          ? `oh ${SMALL_NUMBER_WORDS[minute]}`
          : numberBelowSixtyToWords(minute);

      return `${SMALL_NUMBER_WORDS[hour]} ${spokenMinute}`;
    },
  );

const strengthenWeekdayBoundary = (value: string): string =>
  value.replace(
    /,\s+(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/gi,
    ". $1",
  );

const normalizeSpokenAbbreviations = (value: string): string =>
  value.replace(/\bDr\.(?=\s|$)/giu, "Doctor");

const SPOKEN_INITIALISMS = new Set([
  "AI",
  "BBC",
  "CIA",
  "CPU",
  "DNA",
  "FBI",
  "GPU",
  "HTML",
  "HTTP",
  "HTTPS",
  "IRS",
  "MP3",
  "PDF",
  "TV",
  "UK",
  "UN",
  "URL",
  "US",
  "USA",
  "USB",
  "VIP",
  "XML",
]);

const normalizeDisplayCapitalization = (value: string): string =>
  value.replace(/\b\p{Lu}{2,}\b/gu, (word) =>
    SPOKEN_INITIALISMS.has(word) ? word : word.toLocaleLowerCase("en-US"),
  );

const normalizeSpeechTokens = (value: string): string =>
  normalizeSpokenAbbreviations(
    normalizeClockTimes(normalizeDisplayCapitalization(value)),
  );

const normalizeSceneHeading = (value: string): string => {
  let spoken = value
    .replace(/^INT\.\s*\/\s*EXT\.\s*/i, "Interior and exterior ")
    .replace(/^EXT\.\s*\/\s*INT\.\s*/i, "Exterior and interior ")
    .replace(/^I\s*\/\s*E\.\s*/i, "Interior and exterior ")
    .replace(/^INT\.\s*/i, "Interior ")
    .replace(/^EXT\.\s*/i, "Exterior ")
    .replace(/\s+[-–—]\s+/g, " ");

  const withoutPrefix = spoken.replace(/^(Interior(?: and exterior)?|Exterior(?: and interior)?)\s+/i, "");
  if (withoutPrefix && withoutPrefix === withoutPrefix.toUpperCase()) {
    const prefix = spoken.slice(0, spoken.length - withoutPrefix.length);
    spoken = `${prefix}${withoutPrefix.toLocaleLowerCase("en-US")}`;
  }

  return capitalizeSentenceStarts(
    ensureTerminalPunctuation(normalizeSpeechTokens(spoken)),
  );
};

const normalizeSpokenPunctuation = (value: string): string => {
  const spoken = strengthenWeekdayBoundary(
    normalizeSpeechTokens(value),
  )
    .replace(SPEECH_DASH_BOUNDARY, ". ")
    .replace(/\.{3}|…/g, ". ")
    .replace(/\s+/g, " ")
    .trim();

  return capitalizeSentenceStarts(ensureTerminalPunctuation(spoken));
};

export const prepareTextFragmentForSpeech = (text: string): string =>
  normalizeSpeechTokens(text)
    .replace(SPEECH_DASH_BOUNDARY, " ")
    .replace(/\.{3}|…/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Slice emotion segments from the exact spoken text, after sentence-level case
 * and punctuation preparation. Match content in order (including repeated words)
 * rather than searching case-insensitively for a potentially wrong occurrence.
 * This does not edit the screenplay, change emotion settings, or relax the API's
 * exact-range validation. Different spoken content still fails closed.
 */
export const alignSpeechSegments = <T extends { text: string }>(
  spokenText: string,
  segments: T[],
): T[] => {
  const atoms = (text: string) => [...text.matchAll(/[\p{L}\p{N}\p{M}'’]/gu)];
  const content = (matches: RegExpMatchArray[]) => matches
    .map(match => match[0].replace("’", "'").toLocaleLowerCase("en-US")).join("");
  const spokenAtoms = atoms(spokenText);
  let atomCursor = 0;
  let characterCursor = 0;
  const aligned = segments.map((segment, index) => {
    const segmentAtoms = atoms(segment.text);
    const nextAtom = atomCursor + segmentAtoms.length;
    if (content(segmentAtoms) !== content(spokenAtoms.slice(atomCursor, nextAtom))) {
      throw new Error("The emotional range could not be aligned with the prepared speech text.");
    }
    const lastAtom = spokenAtoms[nextAtom - 1];
    let end = segmentAtoms.length > 0
      ? lastAtom.index! + lastAtom[0].length
      : characterCursor;
    // Keep trailing punctuation with the segment that originally owned it.
    // Sentence preparation may have changed a comma or dash into a period.
    if (index === segments.length - 1) end = spokenText.length;
    else if (/[^\p{L}\p{N}\p{M}'’\s]\s*$/u.test(segment.text)) {
      end = spokenAtoms[nextAtom]?.index ?? spokenText.length;
    }
    const text = spokenText.slice(characterCursor, end).trim();
    atomCursor = nextAtom;
    characterCursor = end;
    return { ...segment, text };
  });
  if (atomCursor !== spokenAtoms.length) {
    throw new Error("The emotional ranges do not cover the prepared speech text.");
  }
  return aligned.filter(segment => segment.text.length > 0);
};

export const prepareTextForSpeech = (
  text: string,
  type: SequenceItem["type"],
): string => {
  const normalizedWhitespace = text.replace(/\s+/g, " ").trim();

  if (!normalizedWhitespace) return normalizedWhitespace;
  if (type === "scene-heading") return normalizeSceneHeading(normalizedWhitespace);

  return normalizeSpokenPunctuation(normalizedWhitespace);
};
